import { FormikHelpers, useFormik } from 'formik';
import { AnnouncementEdit, Announcement } from '../announcement';
import {
  Grid,
  TextField,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Paper,
  Button,
} from '@mui/material';
import { getMediaUrl } from '../../../core/utils/image';
import RichText from '../../../core/ui/input/RichText';

type AnnouncementProps = {
  onSubmit: (
    value: AnnouncementEdit,
    helpers: FormikHelpers<AnnouncementEdit>
  ) => Promise<void>;
  submitting: boolean;
  announcement: Announcement;
};

const EditAnnouncementForm = ({
  onSubmit,
  submitting,
  announcement,
}: AnnouncementProps) => {
  const handleSubmit = async (
    value: AnnouncementEdit,
    helpers: FormikHelpers<AnnouncementEdit>
  ) => {
    await onSubmit(value, helpers);
  };

  const initialValues: AnnouncementEdit = {
    title: announcement.title,
    description: announcement.description,
    content: announcement.content,
    category: announcement.category,
    image: null,
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
  });

  // console.log('Edit Form', initialValues);

  return (
    <>
      <Box sx={{ flexGrow: 1, maxWidth: 800 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={1}>
            <Grid item>
              <Paper sx={{ p: 2, pb: 3 }}>
                <Grid container justifyContent="space-between" spacing={1}>
                  <Grid item alignSelf="flex-start" sx={{ flexGrow: 1 }}>
                    <TextField
                      fullWidth
                      name="title"
                      size="small"
                      type="text"
                      variant="outlined"
                      label="Title"
                      placeholder=""
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      error={Boolean(formik.errors.title)}
                      helperText={formik.errors.title}
                    />
                  </Grid>

                  <Grid item alignSelf="flex-start" sx={{ flexGrow: 1 }}>
                    <TextField
                      fullWidth
                      name="description"
                      size="small"
                      type="text"
                      variant="outlined"
                      label="Description"
                      placeholder=""
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      error={Boolean(formik.errors.description)}
                      helperText={formik.errors.description}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mt: 2 }}
                >
                  <Grid item alignSelf="flex-start" sx={{ flexGrow: 1 }}>
                    <Grid item xs={12}>
                      Content
                    </Grid>
                    <RichText
                      value={formik.values.content || ''}
                      onChange={(e) => {
                        formik.setFieldValue('content', e);
                      }}
                    />
                    {formik.touched.content && formik.errors.content && (
                      <FormHelperText error>
                        {formik.errors.content}
                      </FormHelperText>
                    )}
                  </Grid>
                </Grid>
                <Grid
                  container
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mt: 2 }}
                >
                  <Grid item alignSelf="flex-start" sx={{ flexGrow: 1 }}>
                    <FormControl
                      sx={{ minWidth: 120 }}
                      error={Boolean(formik.errors.category)}
                      fullWidth
                    >
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={formik.values.category}
                        size="small"
                        label="Category"
                        onChange={formik.handleChange}
                        name="category"
                        fullWidth
                      >
                        <MenuItem value="proposal">Call for proposal</MenuItem>
                        <MenuItem value="vacancy">Vacancies</MenuItem>
                        <MenuItem value="training">Training</MenuItem>
                        <MenuItem value="conference">
                          Conferences and workshop
                        </MenuItem>
                        <MenuItem value="fellowship">Fellowship</MenuItem>
                        <MenuItem value="nomination">
                          Nominations and prize
                        </MenuItem>
                        <MenuItem value="scholarship">Scholarship</MenuItem>
                        <MenuItem value="funding_resource">
                          Resource for funding
                        </MenuItem>
                        <MenuItem value="bid">Bid</MenuItem>
                      </Select>
                      {Boolean(formik.errors.category) && (
                        <FormHelperText>
                          {formik.errors.category}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item alignSelf="flex-start" sx={{ flexGrow: 1 }}>
                    <Grid item xs={12}>
                      Image
                    </Grid>
                    <Grid item sx={{ mr: 3 }}>
                      <img
                        src={getMediaUrl(
                          announcement.media.length
                            ? announcement.media[0]
                            : null
                        )}
                        width="256px"
                        alt="cover"
                      />
                    </Grid>
                    <Box>
                      <input
                        type="file"
                        onChange={({ target: { files } }) => {
                          formik.setFieldValue(
                            'image',
                            files && files.length ? files[0] : null
                          );
                        }}
                        name="image"
                      />
                    </Box>
                    {formik.touched.image && formik.errors.image && (
                      <FormHelperText error>
                        {formik.errors.image}
                      </FormHelperText>
                    )}
                  </Grid>
                </Grid>

                <Grid container sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    size="small"
                  >
                    {submitting ? 'Saving' : 'Save'}
                  </Button>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  );
};

export default EditAnnouncementForm;
